/**
 * Methodology Translations Module (Spanish)
 * Research Approach & Data Sources for JCI Development Councils Youth Futures Observatory
 * @module methodology
 * @language es
 */

export const methodology = {
  methodology: {
    // Page Meta
    pageTitle: "Enfoque de Investigación y Fuentes de Datos",
    heroSubtitle: "Inteligencia basada en evidencia empoderando a las Organizaciones Nacionales y Locales de JCI",
    heroDescription: "El Observatorio de Futuros de la Juventud de los Consejos de Desarrollo de JCI está construido sobre una metodología rigurosa y auditable que combina datos verificados de la organización JCI, iniciativas globales de investigación juvenil e índices económicos establecidos.",
    
    // Hero CTAs
    ctaExplore: "Explorar la Metodología",
    ctaContact: "Contactar al Equipo",
    
    // Executive Overview
    executiveOverviewTitle: "Resumen Ejecutivo",
    executiveOverviewText: "Este documento describe el enfoque de investigación, las fuentes de datos y los métodos analíticos utilizados para crear los insights del observatorio. Cada estadística mostrada se rastrea directamente a fuentes verificadas y auditables.",
    coreCommitment: "Cada estadística mostrada se rastrea directamente a fuentes verificadas y auditables.",
    
    // Overview Section
    overviewTitle: "El Framework DASHCity",
    overviewDescription: "Un enfoque integral de 5 fases diseñado para transformar organizaciones en potencias de alto impacto a través de evaluación sistemática, medición y mejora continua.",
    overviewIntro: "DASHCity proporciona un camino estructurado para que las organizaciones identifiquen oportunidades, midan el desempeño, comuniquen valor, establezcan gobernanza y escalen su impacto globalmente.",
    frameworkFeatures: "Framework de 5 Fases DASHCity",
    inclusiveFeature: "Inclusivo - Diseñado para organizaciones de todos los tamaños y regiones",
    scalableFeature: "Escalable - Desde implementación local hasta global",
    
    // Framework Characteristics
    characteristicsTitle: "Principios del Framework",
    characteristics: {
      evidenceBased: "Basado en Evidencia",
      evidenceBasedDesc: "Fundamentado en datos verificados e investigación",
      iterative: "Iterativo",
      iterativeDesc: "Mejora continua a través de ciclos de retroalimentación",
      inclusive: "Inclusivo",
      inclusiveDesc: "Diseñado para consejos de todos los tamaños y regiones",
      scalable: "Escalable",
      scalableDesc: "Desde organizaciones locales hasta implementación global",
      transparent: "Transparente",
      transparentDesc: "Metodología abierta y estándares de datos",
      actionable: "Accionable",
      actionableDesc: "Herramientas prácticas y entregables claros"
    },
    
    // Phase Status Badges
    statusDone: "COMPLETADO",
    statusDoing: "EN PROGRESO",
    statusToDo: "PLANIFICADO",
    
    // Phase Detail Labels
    activitiesLabel: "Actividades",
    deliverablesLabel: "Entregables",
    resultLabel: "Resultado",
    
    // Section Titles  
    dataCollectionTitle: "Metodología de Recopilación de Datos",
    
    // Phase 1: IDENTIFY
    phase1Title: "IDENTIFICAR",
    phase1Period: "Septiembre - Noviembre 2025",
    phase1Objective: "Mapear el ecosistema completo de desarrollo de liderazgo juvenil",
    phase1Description: "La fase de fundación se enfoca en comprender el estado actual de los Consejos de Desarrollo, mapear los recursos existentes e identificar áreas clave de oportunidad.",
    phase1Activities: "Mapeo estratégico, diagnóstico institucional, análisis de brechas, definición del alcance de investigación",
    phase1Deliverables: "Documento de análisis estratégico, Diagnóstico institucional, Inventario de fuentes de datos, Documento de alcance de investigación",
    phase1KeyMetrics: "Tasa de cobertura de consejos, nivel de compromiso de stakeholders, áreas prioritarias identificadas",
    phase1Result: "Justificación estratégica clara para la iniciativa de investigación",
    phase1Status: "done",
    
    // Phase 2: MEASURE
    phase2Title: "MEDIR",
    phase2Period: "Octubre - Diciembre 2025",
    phase2Objective: "Establecer estándares rigurosos de datos y procesos de verificación",
    phase2Description: "La fase de medición implementa una recopilación rigurosa de datos en múltiples fuentes, estableciendo métricas de referencia e indicadores de desempeño.",
    phase2Activities: "Estándares de calidad de datos, verificación de fuentes, establecimiento de línea base, implementación del proceso de validación",
    phase2Deliverables: "Estándares de calidad de datos, Matriz de verificación de fuentes, Documentación de metodología YFI, 40+ puntos de datos auditables",
    phase2KeyMetrics: "Tasa de completitud de datos, cobertura de indicadores, precisión de validación",
    phase2Result: "40+ puntos de datos auditables establecidos",
    phase2Status: "done",
    
    // Phase 3: PROMOTE
    phase3Title: "PROMOVER",
    phase3Period: "Diciembre 2025 - Enero 2026",
    phase3Objective: "Comunicar valor e involucrar a stakeholders",
    phase3Description: "La fase de promoción transforma los insights de medición en narrativas convincentes que comunican el valor e impacto del observatorio.",
    phase3Activities: "Narrativa de valor estratégico, materiales de comunicación, canales de engagement, alcance a stakeholders",
    phase3Deliverables: "Materiales multilingües, Propuestas de valor por stakeholder, Canales de engagement, Guías de contribución",
    phase3KeyMetrics: "Alcance de stakeholders, tasas de engagement, puntuaciones de resonancia del mensaje",
    phase3Result: "Materiales de comunicación multilingües operativos",
    phase3Status: "doing",
    
    // Phase 4: DIRECT
    phase4Title: "DIRIGIR",
    phase4Period: "Enero - Febrero 2026",
    phase4Objective: "Establecer gobernanza y supervisión de calidad",
    phase4Description: "La fase de dirección establece frameworks robustos de gobernanza, define roles y responsabilidades, y crea estructuras operativas sostenibles.",
    phase4Activities: "Estructura de gobernanza, proceso de validación, ciclos de actualización, definiciones de responsabilidad",
    phase4Deliverables: "Estructura de gobernanza, Definiciones de responsabilidad, Lista de verificación de calidad, Cronograma de ciclos de actualización",
    phase4KeyMetrics: "Tasa de adopción de gobernanza, eficiencia de decisiones, niveles de cumplimiento",
    phase4Result: "Estructura de gobernanza con 4 Consejos de Desarrollo",
    phase4Status: "doing",
    
    // Phase 5: SCALE
    phase5Title: "ESCALAR",
    phase5Period: "Enero 2026 - Diciembre 2027+",
    phase5Objective: "Expandir alcance, impacto e influencia global",
    phase5Description: "La fase de escalamiento se enfoca en expandir el alcance de la metodología, amplificar el impacto a través de efectos de red y establecer el framework como referencia global.",
    phase5Activities: "Expansión a 15+ ONs, APIs públicas, alianzas académicas, influencia en políticas",
    phase5Deliverables: "Plan de Despliegue Global, APIs Públicas, Alianzas académicas, Documentación de influencia en políticas",
    phase5KeyMetrics: "Cobertura geográfica, usuarios registrados, citaciones en medios",
    phase5Result: "Plataforma escalando a estatus de autoridad global",
    phase5Status: "todo",
    
    // Data Sources Section
    dataSourcesTitle: "Fuentes de Datos",
    dataSourcesDescription: "Datos completos de fuentes verificadas de JCI e investigación global",
    primaryDataSourcesTitle: "Fuentes de Datos Primarias",
    jciInstitutionalData: "Datos Institucionales JCI",
    yfiGlobalData: "Índice de Futuros de la Juventud y Datos Globales",
    secondarySourcesTitle: "Fuentes de Datos Secundarias",
    
    // Table Column Headers
    sourceColumn: "Fuente",
    coverageColumn: "Cobertura",
    categoryColumn: "Categoría",
    dimensionColumn: "Dimensión",
    weightColumn: "Peso",
    sourcesColumn: "Fuentes",
    criterionColumn: "Criterio",
    exampleColumn: "Ejemplo",
    gapColumn: "Brecha",
    mitigationColumn: "Mitigación",
    
    // Primary Data Source Items
    sourceJCIImpactReport: "Informe de Impacto JCI 2025",
    sourceJCIVision2040: "Plan Estratégico JCI Visión 2040",
    sourceJCIMembershipSurvey: "Encuesta de Membresía JCI 2025",
    coverageImpactReport: "148,122 miembros, 4,641 OLs",
    coverageVision2040: "Objetivos estratégicos hasta 2040",
    coverageSurvey: "3,790 encuestados",
    categoryJciInstitutional: "Institucional JCI",

    // YFI Dimensions Table
    yfiDimensionsTitle: "Dimensiones del Índice de Futuros de la Juventud",
    dimensionMentalHealth: "Salud Mental",
    dimensionWorkforce: "Fuerza Laboral y Educación",
    dimensionEntrepreneurship: "Emprendimiento",
    dimensionEducation: "Educación",
    coverageCountries: "114 países",
    sourceMentalHealth: "OMS, Sapien Labs MHQ",
    sourceWorkforce: "OIT, UNESCO, Banco Mundial",
    sourceEntrepreneurship: "Banco Mundial, GEM, ITU",
    
    // Formula Legend
    formulaLabel: "Fórmula del Índice de Futuros de la Juventud",
    formulaLegendMH: "MH = Salud Mental",
    formulaLegendWF: "WF = Fuerza Laboral y Educación",
    formulaLegendENT: "ENT = Emprendimiento",
    
    // DC Partnership
    dcPartnershipDescription: "Cómo los Consejos de Desarrollo colaboran con el observatorio",
    dcProvidedTitle: "Lo que los CDs Proporcionaron",
    dcNotProvidedTitle: "Lo que los CDs NO Proporcionaron",
    dcProvidedStrategic: "Colaboración estratégica, retroalimentación regional",
    dcProvidedRegional: "Apoyo de difusión en todas las regiones",
    dcProvidedValidation: "Contexto regional sobre hallazgos, confirmación de relevancia",
    dcProvidedPlansOfAction: "Planes de Acción 2025, estrategias regionales",
    dcNotProvidedPrimaryData: "Datos de investigación primaria",
    dcNotProvidedSurveyDesign: "Diseño o ejecución de encuestas",
    dcNotProvidedBaseline: "Recopilación de datos de referencia",
    dcNotProvidedAnalysis: "Análisis de datos",
    
    // Data Collection Section
    dataCollectionDescription: "Cinco áreas clave de recopilación y análisis de datos",
    dataCollectionJCI: "Consolidación de Datos JCI",
    dataCollectionJCIDesc: "Recopiló Informe de Impacto JCI 2025 oficial, extrajo datos de membresía, organizó por región",
    dataCollectionSurvey: "Análisis de Datos de Encuestas",
    dataCollectionSurveyDesc: "Analizó 3,790 encuestados para demografía, motivaciones, brechas de habilidades",
    dataCollectionVision: "Documentación Visión 2040",
    dataCollectionVisionDesc: "Extrajo objetivos de crecimiento, pilares estratégicos, desarrolló planificación de escenarios",
    dataCollectionYFI: "Integración YFI",
    dataCollectionYFIDesc: "Mapeó 114 países JCI a rankings YFI, agregó puntuaciones regionales",
    dataCollectionMentalHealth: "Análisis Profundo de Salud Mental",
    dataCollectionMentalHealthDesc: "Sintetizó datos de OMS, analizó investigación de Sapien Labs, conectó con JCI RISE",
    
    // Verification Checklist Items
    verifySourceIdentifiable: "Fuente identificable",
    verifyDateConfirmed: "Fecha confirmada",
    verifyMethodologyDocumented: "Metodología documentada",
    verifySampleSizeStated: "Tamaño de muestra indicado",
    verifyLimitationsAcknowledged: "Limitaciones reconocidas",
    verifyCalculationsReproducible: "Cálculos reproducibles",
    
    // Zero Synthetic Data
    zeroSyntheticDescription: "El observatorio no contiene datos sintéticos",
    zeroSyntheticItem1: "Sin proyecciones presentadas como hechos actuales",
    zeroSyntheticItem2: "Sin estadísticas generadas por modelos",
    zeroSyntheticItem3: "Sin valores interpolados",
    zeroSyntheticItem4: "Sin cifras estimadas (a menos que estén marcadas explícitamente)",
    zeroSyntheticItem5: "Sin distribuciones asumidas",
    
    // Acknowledged Limitations
    limitationSurveyBias: "Sesgo de respuesta de encuesta",
    mitigationSurveyBias: "Ponderación regional aplicada, sesgo documentado",
    limitationCoverage: "Brechas de cobertura de países YFI",
    mitigationCoverage: "Promedios regionales calculados; brechas anotadas",
    limitationLagging: "Indicadores rezagados",
    mitigationLagging: "Datos más recientes usados (2024-2025)",
    limitationLanguage: "Limitaciones de idioma",
    mitigationLanguage: "Traducción en 5 idiomas; sesgo en inglés anotado",
    
    // Vision 2040 Alignment
    vision2040AlignmentDescription: "Cómo el observatorio apoya la visión estratégica de JCI",
    strategyWhereExpand: "Dónde Expandir",
    strategyWhereExpandDesc: "Expansión prioritaria en regiones de África y Medio Oriente con puntuaciones YFI por debajo de 55",
    strategyWhatPrioritize: "Qué Priorizar",
    strategyWhatPrioritizeDesc: "Dimensión de Salud Mental (peso 40%) como área de enfoque principal",
    strategyHowScale: "Cómo Escalar",
    strategyHowScaleDesc: "Objetivo de adopción tecnológica: 80%+ de engagement digital",
    strategySuccessMeasures: "Medidas de Éxito",
    strategySuccessMeasuresDesc: "Objetivo: Mejorar puntuaciones YFI regionales 4-7 puntos para 2030",
    
    // Transparency Section
    transparencyDescription: "Transparencia completa de metodología y reproducibilidad",
    reproducibilityTitle: "Reproducibilidad",
    reproducibilityDescription: "Cualquier investigador puede reproducir este análisis usando las fuentes y metodología publicadas",
    noProprietaryTitle: "Sin Métodos Propietarios",
    noProprietaryDescription: "Sin cálculos de caja negra; todas las fórmulas y pesos están documentados públicamente",
    
    // Summary Section
    summaryDescription: "Estado actual de implementación de metodología de investigación",
    summaryCompletedItem1: "Fase 1: Identificar",
    summaryCompletedItem2: "Fase 2: Medir",
    summaryCompletedItem3: "Arquitectura de datos",
    summaryCompletedItem4: "Cálculo YFI",
    summaryCompletedItem5: "Cobertura de 114 países",
    summaryCompletedItem6: "Plataforma multilingüe",
    summaryCompletedItem7: "Modelo de alianza DC",
    summaryInProgressItem1: "Fase 3: Promover",
    summaryInProgressItem2: "Fase 4: Dirigir",
    summaryInProgressItem3: "Integración de feedback de usuarios",
    summaryInProgressItem4: "Análisis regionales profundos",
    summaryPlannedItem1: "Fase 5: Escalar",
    summaryPlannedItem2: "Integración UN/WEF",
    summaryPlannedItem3: "Desarrollo de API",
    
    // Data Integrity Section
    dataIntegrityTitle: "Estándares de Integridad de Datos",
    verificationChecklistTitle: "Lista de Verificación",
    zeroSyntheticTitle: "Compromiso con Cero Datos Sintéticos",
    zeroSyntheticException: "Excepción: Las proyecciones de escenarios para la Visión 2040 están claramente etiquetadas como 'escenarios' e ilustrativas, no predicciones.",
    acknowledgedLimitationsTitle: "Limitaciones Reconocidas",
    
    // Verification Checklist Examples
    verifyExampleSource: "JCI Impact Report 2025, p.12",
    verifyExampleDate: "Datos a diciembre de 2024",
    verifyExampleMethodology: "Metodología de encuesta en apéndice",
    verifyExampleSampleSize: "N = 3.790 encuestados",
    verifyExampleLimitations: "Sesgo de idioma inglés notado",
    verifyExampleCalculations: "Fórmula YFI publicada",
    
    // DC Partnership Section
    dcPartnershipTitle: "Alianza con Consejos de Desarrollo",
    partnershipModelTitle: "Modelo de Alianza",
    whatDCsProvided: "Lo que los CDs Proporcionaron",
    whatDCsDidNotProvide: "Lo que los CDs NO Proporcionaron",
    
    // Measurement Modules
    modulesTitle: "Módulos de Medición",
    modulesDescription: "Cuatro áreas temáticas aseguran una evaluación integral del desempeño e impacto de los Consejos de Desarrollo.",
    
    module1Title: "Alineación Estratégica",
    module1Description: "Mide la alineación con la misión, visión y prioridades estratégicas globales de JCI",
    module1Indicators: "Claridad de visión, adopción de misión, calidad del plan estratégico, puntuación de alineación global",
    
    module2Title: "Excelencia Operacional",
    module2Description: "Evalúa la eficiencia operativa, gestión de recursos y salud organizacional",
    module2Indicators: "Utilización de recursos, eficiencia de procesos, satisfacción de miembros, sostenibilidad financiera",
    
    module3Title: "Calidad Educativa",
    module3Description: "Evalúa programas de capacitación, desarrollo de habilidades y efectividad de transferencia de conocimiento",
    module3Indicators: "Puntuaciones de calidad de programas, resultados de aprendizaje, tasas de certificación, efectividad de instructores",
    
    module4Title: "Impacto y Transformación",
    module4Description: "Mide resultados tangibles, impacto en la comunidad y cambio transformacional",
    module4Indicators: "Tasas de finalización de proyectos, alcance de beneficiarios, alineación con ODS, métricas de sostenibilidad",
    
    // Areas of Opportunity
    areasTitle: "Áreas de Oportunidad",
    areasDescription: "La metodología aborda cuatro áreas interconectadas donde los Consejos de Desarrollo pueden maximizar su impacto.",
    
    areaIndividual: "Desarrollo Individual",
    areaIndividualFocus: "Crecimiento personal y desarrollo de liderazgo de los miembros",
    areaIndividualMetrics: "Habilidades adquiridas, posiciones de liderazgo ocupadas, avance profesional",
    
    areaCommunity: "Impacto en la Comunidad",
    areaCommunityFocus: "Cambio positivo en comunidades locales a través de proyectos e iniciativas",
    areaCommunityMetrics: "Proyectos completados, beneficiarios alcanzados, resultados sostenibles",
    
    areaInternational: "Conexión Internacional",
    areaInternationalFocus: "Compromiso global y colaboración intercultural",
    areaInternationalMetrics: "Eventos internacionales, alianzas formadas, participación en intercambios",
    
    areaEntrepreneurial: "Espíritu Emprendedor",
    areaEntrepreneurialFocus: "Innovación, desarrollo de negocios y creación de oportunidades económicas",
    areaEntrepreneurialMetrics: "Negocios apoyados, programas de innovación, impacto económico",
    
    // Data Standards
    dataStandardsTitle: "Estándares de Datos",
    dataStandardsDescription: "Estándares rigurosos aseguran calidad, confiabilidad y comparabilidad de datos en todas las fuentes.",
    
    acceptedTitle: "Criterios de Datos Aceptados",
    acceptedVerifiable: "Verificable a través de fuentes oficiales",
    acceptedAuditable: "Auditable con rastro de documentación claro",
    acceptedTraceable: "Rastreable al punto de recopilación original",
    acceptedAccurate: "Preciso dentro de niveles de tolerancia definidos",
    acceptedCurrent: "Actual (recopilado dentro del plazo especificado)",
    acceptedContextualized: "Contextualizado con metadatos relevantes",
    acceptedCited: "Debidamente citado y atribuido",
    
    notAcceptedTitle: "No Aceptado",
    notAcceptedSynthetic: "Datos sintéticos o generados por IA sin validación",
    notAcceptedUnverifiable: "Afirmaciones o estimaciones no verificables",
    notAcceptedProprietary: "Datos propietarios sin permiso",
    notAcceptedIncomplete: "Conjuntos de datos incompletos sin variables clave",
    notAcceptedBiased: "Muestras sesgadas no representativas de la población",
    
    // Validation Process
    validationTitle: "Proceso de Validación",
    validationDescription: "Una validación rigurosa de 5 pasos asegura la integridad y confiabilidad de los datos.",
    
    validationStep1: "Recopilación Inicial",
    validationStep1Desc: "Datos recopilados usando instrumentos y protocolos estandarizados",
    validationStep2: "Verificación de Calidad",
    validationStep2Desc: "Procedimientos automatizados y manuales de aseguramiento de calidad",
    validationStep3: "Validación Cruzada",
    validationStep3Desc: "Verificación contra múltiples fuentes de datos",
    validationStep4: "Revisión de Expertos",
    validationStep4Desc: "Evaluación y aprobación de expertos en la materia",
    validationStep5: "Certificación Final",
    validationStep5Desc: "Certificación oficial y aprobación de publicación",
    
    // Governance Structure
    governanceTitle: "Propuesta de Estructura de Gobernanza",
    governanceDescription: "Una gobernanza clara asegura responsabilidad, transparencia y toma de decisiones efectiva.",
    
    governanceLevel1: "Junta Internacional JCI",
    governanceLevel1Desc: "Supervisión estratégica y dirección de políticas",
    governanceLevel2: "Consejos de Desarrollo",
    governanceLevel2Desc: "Validación de datos y coordinación regional",
    governanceLevel3: "Equipo Ejecutivo",
    governanceLevel3Desc: "Operaciones e implementación",
    governanceLevel4: "Contribuyentes y Usuarios",
    governanceLevel4Desc: "Retroalimentación de la comunidad y engagement",
    
    // Update Cycles
    updateCyclesTitle: "Ciclos de Actualización",
    updateCycleAnnual: "Anual",
    updateCycleAnnualDesc: "Actualización completa de datos (recopilación Nov, validación Dic, publicación Ene)",
    updateCycleIntermediate: "Intermedio",
    updateCycleIntermediateDesc: "Nueva investigación significativa o innovaciones de programas",
    updateCycleContinuous: "Continuo",
    updateCycleContinuousDesc: "Correcciones de errores, actualizaciones de enlaces, mejoras de plataforma",
    
    // Vision 2040 Alignment
    vision2040AlignmentTitle: "Alineación con JCI Visión 2040",
    strategicIntelligenceTitle: "Función de Inteligencia Estratégica",
    strategicRecommendationsTitle: "Recomendaciones Estratégicas",
    
    // Transparency Section
    transparencyTitle: "Transparencia y Reproducibilidad",
    completeSourceInventoryTitle: "Inventario Completo de Fuentes",
    jciDocuments: "Documentos Oficiales JCI",
    globalResearchSources: "Fuentes de Investigación Global",
    formulaReproducibility: "Cualquier investigador puede reproducir este análisis",
    formulaExplanation: "FII = (0.40 × MH) + (0.30 × WF) + (0.30 × ENT)",
    fullTransparency: "Sin metodologías propietarias, sin cálculos de caja negra, sin suposiciones ocultas.",
    
    // Summary Section
    summaryTitle: "Estado de la Metodología de Investigación",
    summaryCompleted: "COMPLETADO",
    summaryInProgress: "EN PROGRESO",
    summaryPlanned: "PLANIFICADO",
    
    // Expansion Roadmap
    roadmapTitle: "Hoja de Ruta de Expansión",
    roadmapDescription: "Un enfoque por fases para la adopción global y mejora continua.",
    
    roadmap2025: "2025: Fundación",
    roadmap2025Desc: "Implementación piloto con 4 Consejos de Desarrollo, estándares de datos establecidos",
    roadmap2026: "2026: Expansión (BETA)",
    roadmap2026Desc: "15+ ONs contribuyendo, 200+ usuarios, APIs públicas para investigadores",
    roadmap2027: "2027+: Autoridad Global",
    roadmap2027Desc: "Integración UN/WEF/UNESCO, 1,000+ usuarios, influencia en políticas de 10+ países",
    
    // Success Factors
    successTitle: "Factores de Éxito",
    successDescription: "Elementos clave que contribuyen a la implementación exitosa de la metodología.",
    
    successFactor1: "Patrocinio ejecutivo fuerte y compromiso",
    successFactor2: "Comunicación clara y gestión del cambio",
    successFactor3: "Recursos adecuados y equipos dedicados",
    successFactor4: "Enfoque iterativo con retroalimentación continua",
    successFactor5: "Habilitación tecnológica y automatización",
    successFactor6: "Sensibilidad cultural y adaptación regional",
    
    // Lessons Learned
    lessonsTitle: "Lecciones Aprendidas",
    lessonsDescription: "Insights de implementaciones piloto y retroalimentación de stakeholders.",
    
    lesson1: "Comenzar con adoptantes tempranos dispuestos para construir impulso",
    lesson2: "Invertir fuertemente en capacitación y desarrollo de capacidades",
    lesson3: "Equilibrar estandarización con flexibilidad local",
    lesson4: "Celebrar victorias rápidas para mantener el compromiso",
    lesson5: "Incorporar mecanismos de retroalimentación desde el inicio",
    
    // FAQ Section
    faqTitle: "FAQ de la Metodología",
    faqDescription: "Preguntas comunes sobre la metodología de investigación.",
    
    faqQ1: "¿Qué fuentes de datos utiliza el observatorio?",
    faqA1: "El observatorio combina tres fuentes primarias: datos institucionales JCI (Informe de Impacto, Encuesta de Membresía, Visión 2040), el Índice de Futuros Juveniles que cubre 114 países, e investigación global de OMS, OIT, UNESCO, Banco Mundial y otras fuentes establecidas.",
    
    faqQ2: "¿Cómo se asegura la calidad de los datos?",
    faqA2: "Todos los datos deben pasar una lista de verificación de 7 criterios: verificable, auditable, rastreable, preciso, actual, contextualizado y debidamente citado. Un proceso de validación de 5 pasos asegura la integridad antes de la publicación.",
    
    faqQ3: "¿Qué significa 'cero datos sintéticos'?",
    faqA3: "No se presentan proyecciones, estadísticas generadas por modelos, valores interpolados o cifras estimadas como hechos. Todas las métricas mostradas se rastrean directamente a fuentes verificadas. La única excepción son los escenarios de Visión 2040, claramente etiquetados como ilustrativos.",
    
    faqQ4: "¿Con qué frecuencia se actualizan los datos?",
    faqA4: "El observatorio sigue tres ciclos de actualización: actualización completa anual (Nov-Ene), actualizaciones intermedias para nueva investigación significativa, y mejoras continuas para correcciones y actualizaciones de plataforma.",
    
    faqQ5: "¿Pueden los investigadores reproducir el análisis?",
    faqA5: "Sí. Todos los documentos fuente están públicamente disponibles, la lógica de agregación está documentada y la fórmula FII (0.40×MH + 0.30×WF + 0.30×ENT) es transparente. Cualquier investigador puede reproducir y verificar los resultados.",
    
    // CTA Section
    ctaTitle: "¿Listo para Explorar el Observatorio?",
    ctaDescription: "Accede a insights basados en evidencia para apoyar las decisiones estratégicas de tu organización.",
    ctaButton1: "Explorar el Dashboard",
    ctaButton2: "Cómo Contribuir",
    ctaButton3: "Leer el FAQ",
    
    // Source Attribution
    sourceTitle: "Fuente",
    sourceDescription: "Metodología Versión 3.1, Enero 2026",
    sourceNote: "Desarrollado por los Consejos de Desarrollo JCI en colaboración con stakeholders globales"
  }
} as const;
